import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { generatedReportsActions } from 'app-components/flux/generated-reports';
import type RootState from 'app-components/utils/redux-store/rootState';

import {
  createReportConfiguration,
  updateReportConfiguration,
} from '../../report-configurations/reportConfigurationsActions';
import {
  getLoadPending,
  getReportConfigById,
  getReportConfigByUniqueIdentifier,
  getReportConfigByUniqueIdentifierWeb,
} from '../reportConfigurationsSelectors';
import { useDefaultReportConfig } from './useDefaultReportConfig';

type UseEnsureReportForTabOptions = {
  disableAutoInit?: boolean;
};

export const useEnsureReportForTab = (
  currentTabName: string,
  configId?: string,
  options?: UseEnsureReportForTabOptions,
) => {
  const disableAutoInit = options?.disableAutoInit ?? false;
  const dispatch = useDispatch();
  const store = useStore();
  const businessEnabled = useSelector((state: RootState) => entitlementsSelectors.getBusinessEnabled(state));
  const loadPending = useSelector(
    (state: RootState) => getLoadPending(state) || (businessEnabled && businessSelectors.getLoadPending(state)),
  );
  const { getDefaultReportConfig } = useDefaultReportConfig();
  const defaultReportConfig = useMemo(
    () => getDefaultReportConfig(currentTabName),
    [currentTabName, getDefaultReportConfig],
  );

  // Get current report configuration
  const reportConfigurationByTab = useSelector((state: RootState) =>
    getReportConfigByUniqueIdentifierWeb(state, {
      getTabName: () => currentTabName,
    }),
  );
  const reportConfigurationById = useSelector((state: RootState) =>
    configId ? getReportConfigById(state, { id: configId }) : undefined,
  );
  const reportConfiguration = reportConfigurationById ?? reportConfigurationByTab;

  // Extract the logic into a reusable function
  // Uses store.getState() to read fresh config for the requested tab at call time,
  // avoiding stale closures when called from event handlers before re-render.
  const ensureReportForTab = useCallback(
    (tabName: string) => {
      // Get fresh config from store — by ID if configId is provided, otherwise by tab name
      const freshConfig = configId
        ? getReportConfigById(store.getState() as RootState, { id: configId })
        : getReportConfigByUniqueIdentifier(store.getState() as RootState, { name: tabName });

      if (!freshConfig) {
        // When navigating to a specific config by ID, never create a new config —
        // just wait for the config to be loaded.
        if (configId) return;
        const defaultConfig = getDefaultReportConfig(tabName);
        if (defaultConfig) {
          dispatch(
            createReportConfiguration(defaultConfig, {
              props: { generateReport: true, adjustReportConfiguration: true },
            }),
          );
        }
      } else {
        if (freshConfig.id) {
          const hasConflictingDateBounds =
            (freshConfig.dateRange === 'CUSTOM_SINCE' && !!freshConfig.endOn) ||
            (freshConfig.dateRange !== 'CUSTOM' &&
              freshConfig.dateRange !== 'CUSTOM_SINCE' &&
              !!(freshConfig.startOn || freshConfig.endOn));
          const hasInvalidComparisonColumn =
            freshConfig.reportType === 'COMPARISON' && freshConfig.columnType !== 'TIME';
          if (hasConflictingDateBounds || hasInvalidComparisonColumn) {
            dispatch(
              updateReportConfiguration(freshConfig, {
                props: {
                  generateReport: true,
                  location: freshConfig.id,
                  adjustReportConfiguration: true,
                  ...(hasConflictingDateBounds ? { adjustProps: { changedField: 'dateRange' as const } } : {}),
                },
              }),
            );
          } else {
            dispatch(
              generatedReportsActions.generateReport({
                location: freshConfig.id,
                configuration: freshConfig,
              }),
            );
          }
        }
      }
    },
    [configId, dispatch, getDefaultReportConfig, store],
  );

  // Generate report whenever the active tab changes (including first visit).
  // Uses useEffect (not render-phase) to avoid dispatch-during-render cascades
  // that can cause hundreds of generate calls on component remount.
  useEffect(() => {
    if (!disableAutoInit && !loadPending) {
      ensureReportForTab(currentTabName);
    }
  }, [currentTabName, disableAutoInit, loadPending, ensureReportForTab]);

  return {
    reportConfiguration,
    loadPending,
    ensureReportForTab,
    defaultReportConfig,
  };
};
